"use client"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form"
import { useState, useTransition } from "react"
import LoadingButton from "@/components/ui/loading-button"
import { PhoneInput } from "@/components/ui/phone-input"
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp"
import { Input } from "@/components/ui/input"
import { z } from "zod"
import { MessageSquare, Shield, CheckCircle, User, Mail } from "lucide-react"
import { sendOTP, verifyOTP, resendOTP, completeProfile } from "@/server/actions/phone-auth-actions"

// Phone number schema
const phoneSchema = z.object({
    phone: z.string().min(10, "Please enter a valid phone number"),
})

// OTP schema
const otpSchema = z.object({
    otp: z.string().min(6, "Please enter the 6-digit OTP"),
})

type PhoneSchema = z.infer<typeof phoneSchema>
type OtpSchema = z.infer<typeof otpSchema>

export default function PhoneLoginForm({
    fallback,
    onLoginSuccess
}: {
    fallback?: string;
    onLoginSuccess?: () => void;
}) {
    const [isPending, startTransition] = useTransition()
    
    // Separate error states for each step
    const [phoneError, setPhoneError] = useState<string>();
    const [otpError, setOtpError] = useState<string>();
    const [profileError, setProfileError] = useState<string>();
    
    const [step, setStep] = useState<'phone' | 'otp' | 'profile'>('phone')
    const [phoneNumber, setPhoneNumber] = useState<string>('')
    const [userId, setUserId] = useState<string>('')

    // Profile form state
    const [fullname, setFullname] = useState<string>('')
    const [email, setEmail] = useState<string>('')

    const phoneForm = useForm<PhoneSchema>({
        resolver: zodResolver(phoneSchema),
        defaultValues: {
            phone: "",
        },
    })

    const otpForm = useForm<OtpSchema>({
        resolver: zodResolver(otpSchema),
        defaultValues: {
            otp: "",
        },
    })


    async function onPhoneSubmit(values: PhoneSchema) {
        setPhoneError(undefined);
        startTransition(async () => {
            const response = await sendOTP(values.phone, fallback);
            if (response.success) {
                setPhoneNumber(values.phone);
                setStep('otp');
            } else {
                setPhoneError(response.error ?? "Failed to send OTP. Please try again.");
            }
        });
    }

    async function onOtpSubmit(values: OtpSchema) {
        setOtpError(undefined);
        startTransition(async () => {
            const response = await verifyOTP(phoneNumber, values.otp, fallback);
            
            if (response.success && !response.needsProfileCompletion) {
                // Profile is complete
                if (onLoginSuccess) {
                    // If callback provided, call it instead of redirecting
                    onLoginSuccess();
                } else {
                    // Otherwise redirect as before
                    window.location.href = fallback ?? "/";
                }
            } else if (response.needsProfileCompletion && response.userId) {
                // User needs to complete profile
                setUserId(response.userId);
                setStep('profile');
            } else if (response.error) {
                setOtpError(response.error);
            }
        });
    }

    async function onProfileSubmit(e: React.FormEvent) {
        e.preventDefault();
        setProfileError(undefined);

        // Client-side validation
        if (!fullname || fullname.trim().length < 2) {
            setProfileError("Please enter your full name (at least 2 characters)");
            return;
        }

        if (!email?.includes('@')) {
            setProfileError("Please enter a valid email address");
            return;
        }

        startTransition(async () => {
            const response = await completeProfile(userId, fullname.trim(), email.trim(), fallback);
            if (response.success) {
                // Profile completed successfully
                if (onLoginSuccess) {
                    // If callback provided, call it instead of redirecting
                    onLoginSuccess();
                } else {
                    // Otherwise redirect as before
                    window.location.href = fallback ?? "/";
                }
            } else if (response.error) {
                setProfileError(response.error);
            }
        });
    }

    const handleBackToPhone = () => {
        setStep('phone')
        setPhoneError(undefined)
        setOtpError(undefined)
        otpForm.reset()
    }

    const handleBackToOtp = () => {
        setStep('otp')
        setOtpError(undefined)
        setProfileError(undefined)
        setFullname('')
        setEmail('')
    }

    if (step === 'profile') {
        return (
            <div className="w-full space-y-6">
                {/* Profile Completion Header */}
                <div className="text-center space-y-3">
                    <div className="flex justify-center">
                        <div className="bg-blue-100 p-3 rounded-full">
                            <User className="size-8 text-blue-600" />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <h3 className="text-xl lg:text-2xl font-semibold text-primary-text">Complete Your Profile</h3>
                        <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                            Please provide your full name and email to complete your registration
                        </p>
                    </div>
                </div>

                <form onSubmit={onProfileSubmit} className="space-y-5 w-full">
                    {profileError && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                            <p className="text-center text-red-600 text-sm">{profileError}</p>
                        </div>
                    )}

                    <div className="space-y-3">
                        <label className="lg:text-lg text-primary-text font-medium block">
                            Full Name
                        </label>
                        <div className="relative">
                            <Input 
                                type="text"
                                placeholder="Enter your full name"
                                value={fullname}
                                onChange={(e) => setFullname(e.target.value)}
                                className="h-[3rem] peer pl-10"
                                autoComplete="name"
                                required
                            />
                            <User className="size-5 text-muted-foreground absolute top-4 left-3 pointer-events-none" />
                        </div>
                        {fullname.trim().length > 0 && fullname.trim().length < 2 && (
                            <p className="text-sm text-red-600">Full name must be at least 2 characters</p>
                        )}
                    </div>

                    <div className="space-y-3">
                        <label className="lg:text-lg text-primary-text font-medium block">
                            Email Address
                        </label>
                        <div className="relative">
                            <Input 
                                type="email"
                                placeholder="Enter your email address"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="h-[3rem] peer pl-10"
                                autoComplete="email"
                                required
                            />
                            <Mail className="size-5 text-muted-foreground absolute top-4 left-3 pointer-events-none" />
                        </div>
                        {email.length > 0 && !email.includes('@') && (
                            <p className="text-sm text-red-600">Please enter a valid email address</p>
                        )}
                    </div>

                    <div className="flex flex-col gap-4">
                        <LoadingButton 
                            loading={isPending} 
                            type="submit" 
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12 text-lg font-medium"
                            disabled={!fullname.trim() || fullname.trim().length < 2 || !email.trim() || !email.includes('@')}
                        >
                            {isPending ? "Completing..." : "Complete Registration"}
                        </LoadingButton>
                        
                        <button
                            type="button"
                            onClick={handleBackToOtp}
                            className="text-sm text-muted-foreground hover:text-primary-text underline"
                        >
                            Back to OTP verification
                        </button>
                    </div>
                </form>
            </div>
        )
    }

    if (step === 'phone') {
        return (
            <div className="w-full space-y-6">
                {/* WhatsApp Info Section */}
                <div className="text-center space-y-3">
                    <div className="flex justify-center">
                        <div className="bg-green-100 p-3 rounded-full">
                            <MessageSquare className="size-8 text-green-600" />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <h2 className="text-xl lg:text-2xl font-semibold text-primary-text">Login with WhatsApp</h2>
                        <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                            We&apos;ll send a verification code to your WhatsApp number to ensure secure access
                        </p>
                    </div>
                </div>

                {/* Benefits/Features */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
                    <div className="flex items-center gap-3">
                        <Shield className="size-5 text-green-600" />
                        <span className="text-sm font-medium text-green-800">Secure & Instant Verification</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <CheckCircle className="size-5 text-green-600" />
                        <span className="text-sm font-medium text-green-800">No password required</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <MessageSquare className="size-5 text-green-600" />
                        <span className="text-sm font-medium text-green-800">Delivered via WhatsApp</span>
                    </div>
                </div>

                <Form {...phoneForm}>
                    <form onSubmit={phoneForm.handleSubmit(onPhoneSubmit)} className="space-y-5 w-full">
                        {phoneError && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                <p className="text-center text-red-600 text-sm">{phoneError}</p>
                            </div>
                        )}
                        <FormField
                            control={phoneForm.control}
                            name="phone"
                            render={({ field }) => (
                                <FormItem className="space-y-3">
                                    <FormLabel className="lg:text-lg text-primary-text font-medium">
                                        Enter your WhatsApp number
                                    </FormLabel>
                                    <FormControl>
                                        <div className="relative">
                                            <PhoneInput 
                                                placeholder="Your WhatsApp number" 
                                                {...field} 
                                                className="h-[3rem] peer"
                                                defaultCountry="IN"
                                            />
                                            <MessageSquare className="peer-focus-visible:text-green-600 size-5 text-muted-foreground absolute top-4 right-3 pointer-events-none" />
                                        </div>
                                    </FormControl>
                                    <p className="text-xs text-muted-foreground">
                                        Make sure this number is linked to your WhatsApp account
                                    </p>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <LoadingButton 
                            loading={isPending} 
                            type="submit" 
                            className="w-full bg-green-600 hover:bg-green-700 text-white h-12 text-lg font-medium"
                        >
                            {isPending ? "Sending OTP..." : "Send WhatsApp OTP"}
                        </LoadingButton>
                    </form>
                </Form>
            </div>
        )
    }

    return (
        <div className="w-full space-y-6">
            {/* WhatsApp OTP Header */}
            <div className="text-center space-y-3">
                <div className="flex justify-center">
                    <div className="bg-green-100 p-3 rounded-full">
                        <MessageSquare className="size-8 text-green-600" />
                    </div>
                </div>
                <div className="space-y-2">
                    <h3 className="text-xl lg:text-2xl font-semibold text-primary-text">Verify WhatsApp OTP</h3>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 mx-auto max-w-sm">
                        <p className="text-sm text-green-800">
                            We&apos;ve sent a 6-digit verification code to your WhatsApp
                        </p>
                        <p className="text-sm font-medium text-green-900 mt-1">{phoneNumber}</p>
                    </div>
                </div>
            </div>

            <Form {...otpForm}>
                <form onSubmit={otpForm.handleSubmit(onOtpSubmit)} className="space-y-6 w-full">
                    {otpError && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                            <p className="text-center text-red-600 text-sm">{otpError}</p>
                        </div>
                    )}

                    <FormField
                        control={otpForm.control}
                        name="otp"
                        render={({ field }) => (
                            <FormItem className="flex flex-col items-center space-y-4">
                                <FormLabel className="lg:text-lg text-primary-text font-medium">Enter 6-digit OTP</FormLabel>
                                <FormControl>
                                    <InputOTP maxLength={6} {...field}>
                                        <InputOTPGroup className="gap-3">
                                            <InputOTPSlot index={0} className="h-14 w-14 text-xl font-semibold border-2 border-green-200 focus:border-green-500" />
                                            <InputOTPSlot index={1} className="h-14 w-14 text-xl font-semibold border-2 border-green-200 focus:border-green-500" />
                                            <InputOTPSlot index={2} className="h-14 w-14 text-xl font-semibold border-2 border-green-200 focus:border-green-500" />
                                            <InputOTPSlot index={3} className="h-14 w-14 text-xl font-semibold border-2 border-green-200 focus:border-green-500" />
                                            <InputOTPSlot index={4} className="h-14 w-14 text-xl font-semibold border-2 border-green-200 focus:border-green-500" />
                                            <InputOTPSlot index={5} className="h-14 w-14 text-xl font-semibold border-2 border-green-200 focus:border-green-500" />
                                        </InputOTPGroup>
                                    </InputOTP>
                                </FormControl>
                                <p className="text-xs text-muted-foreground text-center">
                                    Check your WhatsApp messages for the verification code
                                </p>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <div className="flex flex-col gap-4">
                        <LoadingButton 
                            loading={isPending} 
                            type="submit" 
                            className="w-full bg-green-600 hover:bg-green-700 text-white h-12 text-lg font-medium"
                            disabled={!otpForm.watch("otp") || otpForm.watch("otp").length < 6}
                        >
                            {isPending ? "Verifying..." : "Verify & Login"}
                        </LoadingButton>
                        
                        <div className="flex flex-col gap-3 text-center">
                            <button
                                type="button"
                                onClick={async () => {
                                    setOtpError(undefined);
                                    const response = await resendOTP(phoneNumber);
                                    if (response.error) {
                                        setOtpError(response.error);
                                    }
                                }}
                                className="text-sm text-green-600 hover:text-green-700 hover:underline font-medium"
                            >
                                Resend WhatsApp OTP
                            </button>
                            
                            <button
                                type="button"
                                onClick={handleBackToPhone}
                                className="text-sm text-muted-foreground hover:text-primary-text underline"
                            >
                                Change WhatsApp number
                            </button>
                        </div>
                    </div>
                </form>
            </Form>

            {/* Help text */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                <p className="text-xs text-blue-800">
                    <strong>Not receiving the code?</strong> Make sure WhatsApp is installed and your number is active
                </p>
            </div>
        </div>
    )
}